import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Send, CheckCircle2, Phone } from "lucide-react";
import { API, inputCls, btnPrimary } from "@/lib/studioApi";
import { PhotoUploader } from "@/components/PhotoUploader";

const empty = { name: "", phone: "", email: "", address: "", postal_code: "", city: "", description: "", media_ids: [], website: "" };

export default function PublicRequest() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { axios.get(`${API}/demande/${slug}`).then(({ data }) => setInfo(data)).catch(() => setError("Cette page n'existe pas.")); }, [slug]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await axios.post(`${API}/demande/${slug}`, form); setSent(true); }
    catch (err) { setError(err.response?.data?.detail || "Envoi impossible, réessayez."); }
    finally { setBusy(false); }
  };

  if (!info && !error) return <div className="min-h-screen bg-[#F6F8FD]" />;
  return (
    <div data-testid="public-request-page" className="font-body min-h-screen bg-[#F6F8FD] px-4 py-8 text-[#0F2645]">
      <div className="mx-auto max-w-xl">
        {info && (
          <header className="flex items-center gap-3">
            {info.logo_url ? <img src={`${API.replace(/\/api$/, "")}${info.logo_url}`} alt="" className="h-12 w-auto rounded-lg bg-white p-1" /> : <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#0F2645] font-heading text-lg font-bold text-white">{(info.company || "D")[0]}</span>}
            <div>
              <h1 data-testid="public-request-company" className="font-heading text-xl font-bold sm:text-2xl">{info.company}</h1>
              <p className="text-sm text-[#5C6A87]">{[info.trade, info.city].filter(Boolean).join(" · ")}</p>
            </div>
          </header>
        )}
        {sent ? (
          <div data-testid="public-request-sent" className="mt-8 rounded-3xl border border-emerald-200 bg-white p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h2 className="mt-3 font-heading text-lg font-bold">Demande envoyée</h2>
            <p className="mt-2 text-sm text-[#5C6A87]">{info?.company} a reçu votre demande et vous recontacte rapidement avec un devis.</p>
            {info?.phone && <a href={`tel:${info.phone}`} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#3A5CC8]"><Phone className="h-4 w-4" /> {info.phone}</a>}
          </div>
        ) : info ? (
          <form onSubmit={submit} className="mt-6 rounded-3xl border border-[#E2E7F7] bg-white p-5 shadow-[0_8px_30px_rgba(15,38,69,0.06)] sm:p-7">
            <h2 className="font-heading text-lg font-bold">Demander un devis gratuit</h2>
            <p className="mt-1 text-sm text-[#5C6A87]">Décrivez vos travaux, ajoutez des photos : vous recevez un devis clair et rapide.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input data-testid="req-name" className={inputCls} placeholder="Votre nom *" required value={form.name} onChange={set("name")} />
              <input data-testid="req-phone" className={inputCls} placeholder="Téléphone *" value={form.phone} onChange={set("phone")} />
              <input data-testid="req-email" type="email" className={`${inputCls} sm:col-span-2`} placeholder="E-mail" value={form.email} onChange={set("email")} />
              <input data-testid="req-address" className={`${inputCls} sm:col-span-2`} placeholder="Adresse des travaux" value={form.address} onChange={set("address")} />
              <input data-testid="req-postal" className={inputCls} placeholder="Code postal" value={form.postal_code} onChange={set("postal_code")} />
              <input data-testid="req-city" className={inputCls} placeholder="Commune" value={form.city} onChange={set("city")} />
            </div>
            <textarea data-testid="req-description" rows={5} className={`${inputCls} mt-3`} placeholder="Décrivez vos travaux : quoi, où, surface, âge du logement, délai souhaité… *" required value={form.description} onChange={set("description")} />
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={set("website")} />
            <div className="mt-3"><PhotoUploader photos={form.media_ids} onChange={(ids) => setForm((f) => ({ ...f, media_ids: ids }))} max={6} testId="req-photos" hint="Photos de la situation actuelle (facultatif)" /></div>
            {error && <p data-testid="req-error" className="mt-3 text-sm text-red-600">{error}</p>}
            <button type="submit" data-testid="req-submit" disabled={busy} className={`${btnPrimary} mt-4 w-full justify-center`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer ma demande</button>
            <p className="mt-3 text-center text-[11px] text-[#93A0BC]">Vos données ne servent qu'à établir votre devis. Page sécurisée by Devisto.</p>
          </form>
        ) : <p data-testid="req-notfound" className="mt-8 text-center text-sm text-[#5C6A87]">{error}</p>}
      </div>
    </div>
  );
}
